import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Viewer, Worker, SpecialZoomLevel } from "@react-pdf-viewer/core";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.js";
import { loadPatrolHotspotPdfViewerUrl } from "./patrolHotspotPdf.js";

import "@react-pdf-viewer/core/lib/styles/index.css";

/**
 * @param {{ url: string, title?: string, frameHeight: number, onOpenExternal: () => void }} props
 */
export function PatrolHotspotPdfPanel({
  url,
  title,
  frameHeight,
  onOpenExternal,
}) {
  const [viewerUrl, setViewerUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let blobUrl = "";
    let cancelled = false;

    setLoading(true);
    setError(null);
    setViewerUrl("");

    loadPatrolHotspotPdfViewerUrl(url)
      .then((resolved) => {
        if (cancelled) {
          if (String(resolved).startsWith("blob:")) {
            URL.revokeObjectURL(resolved);
          }
          return;
        }
        if (String(resolved).startsWith("blob:")) {
          blobUrl = resolved;
        }
        setViewerUrl(resolved);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load PDF.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (blobUrl.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [url]);

  if (loading) {
    return (
      <Box
        sx={{
          height: frameHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f3f4f6",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#64748B" }} />
      </Box>
    );
  }

  if (error || !viewerUrl) {
    return (
      <Box
        sx={{
          height: frameHeight,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          px: 2,
          bgcolor: "#f8fafc",
        }}
      >
        <Typography
          sx={{ fontSize: 13, color: "#374151", textAlign: "center", lineHeight: 1.5 }}
        >
          {error || "PDF preview is not available."}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={onOpenExternal}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Open {title || "PDF"}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: frameHeight,
        bgcolor: "#fff",
        overflow: "hidden",
        "& .rpv-core__viewer": { height: "100%" },
      }}
    >
      <Worker workerUrl={workerSrc}>
        <Viewer fileUrl={viewerUrl} defaultScale={SpecialZoomLevel.PageFit} />
      </Worker>
    </Box>
  );
}
